import { useState, useCallback } from 'react';
import { type Address, erc20Abi, erc721Abi, parseUnits } from 'viem';
import { readContract, writeContract, getAccount, waitForTransactionReceipt } from '@wagmi/core';
import { config } from '@/config/index';
import { AssetType } from '@/components/shared/ApprovalTransactionFlow';
import toast from '@/util/toast';
import { shibaEternityAbi } from '@/constants/abi/shibaEternityAbi';

/**
 * Hook to handle ERC20 token approval flow
 */
export const useERC20ApprovalFlow = (chainId: number) => {
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);

  /**
   * Check if ERC20 token approval is needed
   */
  const checkERC20Approval = useCallback(async ({
    assetAddress,
    spenderAddress,
    amount
  }: {
    assetAddress: Address;
    spenderAddress: Address;
    amount: bigint;
  }): Promise<boolean> => {
    try {
      const account = getAccount(config);
      if (!account?.address) {
        throw new Error('No connected account');
      }

      // Read current allowance
      const allowance = await readContract(config, {
        address: assetAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [account.address as Address, spenderAddress],
        chainId
      });

      // Compare allowance with amount
      return BigInt(allowance) < amount;
    } catch (error) {
      console.error('Error checking ERC20 approval:', error);
      return false;
    }
  }, [chainId]);

  /**
   * Execute ERC20 token approval
   */
  const executeERC20Approval = useCallback(async ({
    assetAddress: assetAddress_0,
    spenderAddress: spenderAddress_0,
    amount: amount_0
  }: {
    assetAddress: Address;
    spenderAddress: Address;
    amount: bigint;
  }) => {
    try {
      // We approve max uint256 to save on future gas costs
      const maxUint256 = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');

      // Write approval transaction
      const hash = await writeContract(config, {
        address: assetAddress_0,
        abi: erc20Abi,
        functionName: 'approve',
        args: [spenderAddress_0, maxUint256],
        chainId
      });
      return {
        hash,
        success: true
      };
    } catch (error_0) {
      console.error('Error executing ERC20 approval:', error_0);
      return {
        success: false
      };
    }
  }, [chainId]);
  return {
    isApprovalDialogOpen,
    setIsApprovalDialogOpen,
    checkERC20Approval,
    executeERC20Approval
  };
};

/**
 * Hook to handle NFT (ERC721) approval flow
 */
export const useNFTApprovalFlow = (chainId: number) => {
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);

  /**
   * Check if NFT approval is needed
   */
  const checkNFTApproval = useCallback(async ({
    assetAddress,
    spenderAddress,
    tokenId
  }: {
    assetAddress: Address;
    spenderAddress: Address;
    tokenId: bigint;
  }): Promise<boolean> => {
    try {
      // First check if contract is approved for all
      const account = getAccount(config);
      if (!account?.address) {
        throw new Error('No connected account');
      }

      // Check isApprovedForAll
      const isApprovedForAll = await readContract(config, {
        address: assetAddress,
        abi: erc721Abi,
        functionName: 'isApprovedForAll',
        args: [account.address as Address, spenderAddress],
        chainId
      });
      if (isApprovedForAll) {
        return false; // No approval needed
      }

      // Check if specific token is approved
      const approvedAddress = await readContract(config, {
        address: assetAddress,
        abi: erc721Abi,
        functionName: 'getApproved',
        args: [tokenId],
        chainId
      });
      return approvedAddress !== spenderAddress;
    } catch (error) {
      console.error('Error checking NFT approval:', error);
      return false;
    }
  }, [chainId]);

  /**
   * Execute NFT approval
   */
  const executeNFTApproval = useCallback(async ({
    assetAddress: assetAddress_0,
    spenderAddress: spenderAddress_0,
    tokenId: tokenId_0,
    approveAll = false
  }: {
    assetAddress: Address;
    spenderAddress: Address;
    tokenId?: bigint;
    approveAll?: boolean;
  }) => {
    try {
      let hash;

      // If approveAll is true or no tokenId is provided, use setApprovalForAll
      if (approveAll || !tokenId_0) {
        hash = await writeContract(config, {
          address: assetAddress_0,
          abi: erc721Abi,
          functionName: 'setApprovalForAll',
          args: [spenderAddress_0, true],
          chainId
        });
      } else {
        // Otherwise approve specific token
        hash = await writeContract(config, {
          address: assetAddress_0,
          abi: erc721Abi,
          functionName: 'approve',
          args: [spenderAddress_0, tokenId_0],
          chainId
        });
      }
      return {
        hash,
        success: true
      };
    } catch (error_0) {
      console.error('Error executing NFT approval:', error_0);
      return {
        success: false
      };
    }
  }, [chainId]);
  return {
    isApprovalDialogOpen,
    setIsApprovalDialogOpen,
    checkNFTApproval,
    executeNFTApproval
  };
};

/**
 * This is the main universal approval hook that handles both ERC20 and NFT approvals
 */
export const useApprovalFlow = (chainId: number) => {
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);

  // Import both approval hooks
  const {
    checkERC20Approval,
    executeERC20Approval
  } = useERC20ApprovalFlow(chainId);
  const {
    checkNFTApproval,
    executeNFTApproval
  } = useNFTApprovalFlow(chainId);

  /**
   * Generic function to check if approval is needed
   */
  const checkNeedsApproval = useCallback(async ({
    assetAddress,
    spenderAddress,
    amount,
    tokenId,
    assetType
  }: {
    assetAddress: Address;
    spenderAddress: Address;
    amount?: bigint;
    tokenId?: bigint;
    assetType: AssetType;
  }) => {
    try {
      switch (assetType) {
        case AssetType.ERC20:
          if (!amount) throw new Error('Amount is required for ERC20 approval');
          return checkERC20Approval({
            assetAddress,
            spenderAddress,
            amount
          });
        case AssetType.ERC721:
          if (!tokenId) throw new Error('TokenId is required for NFT approval');
          return checkNFTApproval({
            assetAddress,
            spenderAddress,
            tokenId
          });
        case AssetType.ERC1155:
          // For ERC1155, we check isApprovedForAll since it doesn't have per-token approval
          try {
            const account = getAccount(config);
            if (!account?.address) {
              throw new Error('No connected account');
            }
            console.log('ERC1155 approval check - Starting:', {
              assetAddress,
              owner: account.address,
              spender: spenderAddress,
              chainId
            });
            const isApprovedForAll = await readContract(config, {
              address: assetAddress,
              abi: shibaEternityAbi,
              functionName: 'isApprovedForAll',
              args: [account.address as Address, spenderAddress],
              chainId
            });
            console.log('ERC1155 approval check - Result:', {
              assetAddress,
              owner: account.address,
              spender: spenderAddress,
              isApprovedForAll,
              needsApproval: !isApprovedForAll,
              rawResult: isApprovedForAll
            });
            return !isApprovedForAll; // Need approval if not approved for all
          } catch (error_0) {
            console.error('Error checking ERC1155 approval - Full error:', {
              error: error_0,
              message: error_0 instanceof Error ? error_0.message : 'Unknown error',
              assetAddress,
              spenderAddress,
              chainId
            });
            // Return true to indicate approval is needed when we can't check
            return true;
          }
        default:
          throw new Error(`Unsupported asset type: ${assetType}`);
      }
    } catch (error) {
      console.error('Error checking approval:', error);
      toast.error(`Failed to check approval status: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  }, [checkERC20Approval, checkNFTApproval, chainId]);

  /**
   * Generic function to execute approval transaction
   */
  const executeApproval = useCallback(async ({
    assetAddress: assetAddress_0,
    spenderAddress: spenderAddress_0,
    amount: amount_0,
    tokenId: tokenId_0,
    assetType: assetType_0
  }: {
    assetAddress: Address;
    spenderAddress: Address;
    amount?: bigint;
    tokenId?: bigint;
    assetType: AssetType;
  }) => {
    try {
      switch (assetType_0) {
        case AssetType.ERC20:
          if (!amount_0) throw new Error('Amount is required for ERC20 approval');
          return executeERC20Approval({
            assetAddress: assetAddress_0,
            spenderAddress: spenderAddress_0,
            amount: amount_0
          });
        case AssetType.ERC721:
          return executeNFTApproval({
            assetAddress: assetAddress_0,
            spenderAddress: spenderAddress_0,
            tokenId: tokenId_0,
            // Use approveAll if no tokenId provided
            approveAll: !tokenId_0
          });
        case AssetType.ERC1155:
          // ERC1155 uses setApprovalForAll like ERC721
          try {
            const hash = await writeContract(config, {
              address: assetAddress_0,
              abi: shibaEternityAbi,
              functionName: 'setApprovalForAll',
              args: [spenderAddress_0, true],
              chainId
            });
            return {
              hash,
              success: true
            };
          } catch (error_2) {
            console.error('Error executing ERC1155 approval:', error_2);
            return {
              success: false
            };
          }
        default:
          throw new Error(`Unsupported asset type: ${assetType_0}`);
      }
    } catch (error_1) {
      console.error('Error executing approval:', error_1);
      toast.error(`Failed to execute approval: ${error_1 instanceof Error ? error_1.message : 'Unknown error'}`);
      return {
        success: false
      };
    }
  }, [executeERC20Approval, executeNFTApproval, chainId]);
  return {
    isApprovalDialogOpen,
    setIsApprovalDialogOpen,
    checkNeedsApproval,
    executeApproval
  };
};
export default useApprovalFlow;